import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { ERoleAdminString } from '../../../../shared/data/role.enum';
import { DeviceService } from '../../services/device.service';
import { DeviceLicenseService } from '../../services/deviceLicense.service';
import { SessionService } from '../../../../shared/services/session.service';
import { DeviceTypeService } from '../../services/deviceType.service';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { IDeviceType, ISetup } from '../../models/device.interface';
import { ICompany } from '../../../companies/models/company.interface';
import { CompanyService } from '../../../companies/services/company.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { EMessageType } from '../../../../shared/data/response-default.interface';
import { SetupService } from '../../services/setup.service';
import { canPickCompany } from '../../../../shared/utilities/company-scope';
import { interceptorAvisouErro } from 'src/app/shared/http-interceptor/http-interceptor.module';

@Component({
  selector: 'app-add-new-device',
  templateUrl: './add-new-device.component.html',
  styleUrls: ['./add-new-device.component.scss']
})
export class AddNewDeviceComponent implements OnInit {
  ERoleAdminString = ERoleAdminString;
  formOpen = false;
  formDevice = new UntypedFormGroup({
    name: new UntypedFormControl('', [Validators.required]),
    companyId: new UntypedFormControl(null, [Validators.required]),
    deviceTypeId: new UntypedFormControl(null, [Validators.required]),
    setupId: new UntypedFormControl(null, [Validators.required]),
    hardwareId: new UntypedFormControl('', [Validators.required]),
    equipmentId: new UntypedFormControl('', [Validators.required]),
    contactName: new UntypedFormControl('', [Validators.required]),
    contactPhone: new UntypedFormControl('', [Validators.required]),
    licenseKey: new UntypedFormControl('', [Validators.required]),
    expirationDate: new UntypedFormControl(''),
    observation: new UntypedFormControl(''),
    paymentTerminalId: new UntypedFormControl('')
  });
  minDate: string = new Date().toISOString().split('T')[0];
  deviceTypes: IDeviceType[] = [];
  companies: ICompany[] = [];
  setupsOptions: ISetup[] = [];
  // Usuário de empresa fica fixo na própria: sem seletor de empresa nem de setup.
  podeEscolherEmpresa = false;
  private empresaFixa?: string;

  @Output() reloadDevices = new EventEmitter();
  constructor(
    private readonly setupService: SetupService,
    private readonly deviceService: DeviceService,
    private readonly deviceLicenseService: DeviceLicenseService,
    private readonly deviceTypeService: DeviceTypeService,
    private readonly companyService: CompanyService,
    private readonly sessionService: SessionService,
    private readonly toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.formDevice.get('setupId')?.disable();
    this.formDevice.get('licenseKey')?.disable();
    this.deviceTypeService.listDevicesTypes().subscribe((response) => {
      this.deviceTypes = response.data;
    });

    const session = this.sessionService.getSession();
    this.podeEscolherEmpresa = canPickCompany(session);

    // Usuário de empresa não tem seletor, e Company/ListActive responde 403 para ele.
    if (this.podeEscolherEmpresa) {
      this.companyService.listarEmpresasAtivas().subscribe({
        next: (response) => {
          this.companies = response.data;
        },
        error: (erro) => {
          this.companies = [];
          if (!interceptorAvisouErro(erro)) {
            this.toastService.toast('Erro ao carregar as empresas. Tente novamente.', EMessageType.Error);
          }
        }
      });
    }

    if (session?.companyId) {
      this.formDevice.get('companyId')?.setValue(session.companyId);
      this.empresaFixa = this.podeEscolherEmpresa ? undefined : session.companyId;
    }

    this.formDevice.get('companyId')?.valueChanges.subscribe((companyId) => {
      this.formDevice.get('setupId')?.patchValue(null);
      if (companyId) {
        this.setupService.listSetupsActiveByCompany(companyId).subscribe((response) => {
          if (response.data) {
            this.setupsOptions = response.data;
          }
          this.formDevice.get('setupId')?.enable();
        });
      } else {
        this.formDevice.get('setupId')?.disable();
      }
    });
  }

  public generateLicense(): void {
    this.deviceLicenseService.generateKey().subscribe((response) => {
      if (response.data) {
        this.formDevice.get('licenseKey')?.setValue(response.data);
      }
    });
  }

  public copyLicenseKey(): void {
    const licenseKey = this.formDevice.get('licenseKey')?.value;
    if (licenseKey) {
      navigator.clipboard.writeText(licenseKey);
      this.toastService.toast('Chave de licença copiada para a área de transferência.', EMessageType.Success);
    }
  }

  public saveDevice(): void {
    if (this.formDevice.invalid) {
      this.toastService.toast('Preencha todos os campos obrigatórios antes de salvar.', EMessageType.Error);
      this.formDevice.markAllAsTouched();
      return;
    }

    if (this.formDevice.valid) {
      this.deviceService.addDevice(this.formDevice.getRawValue()).subscribe((resposta) => {
        if (resposta.messages && resposta.messages.length > 0) {
          this.toastService.toast(resposta.messages);
        }

        if (resposta.isSucceed) {
          this.formOpen = false;
          this.formDevice.reset();
          // reset() limpa a empresa fixa; sem ela o próximo cadastro fica travado no campo obrigatório escondido.
          if (this.empresaFixa) {
            this.formDevice.get('companyId')?.setValue(this.empresaFixa, { emitEvent: false });
          }
          this.reloadDevices.emit();
        }
      });
    }
  }
}
